"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet } from "react-router-dom";
import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { useAmbientBgm } from "@/hooks/useAmbientBgm";
import { ProfileSetupGate } from "@/components/ProfileSetupGate";
import { UiTapSoundBridge } from "@/components/UiTapSoundBridge";
import { useHydratedSession } from "@/hooks/useHydratedSession";
import { guestDevLogin } from "@/lib/auth/guestDevLogin";
import { persistOfflineGuestUuidFromSessionToken } from "@/lib/api";
import { FrvrRoot } from "@/components/FrvrRoot";
import { useSession } from "@/stores/session";
import {
  beginPlatformAudioSuspend,
  endPlatformAudioSuspend,
} from "@/lib/audio";
import { syncBgmPlaybackVolume } from "@/lib/audio/play";

function SessionHydration() {
  useLayoutEffect(() => {
    void useSession.persist.rehydrate();
  }, []);
  return null;
}

/** No login screen: mint or restore a persisted guest session once storage has rehydrated. */
function EnsureGuestSession() {
  const hydrated = useHydratedSession();
  const token = useSession((s) => s.token);
  const setAuth = useSession((s) => s.setAuth);

  useEffect(() => {
    if (!hydrated || token) return;
    let cancelled = false;
    void (async () => {
      if (useSession.getState().token) return;
      try {
        const res = await guestDevLogin();
        if (cancelled || useSession.getState().token) return;
        setAuth(res.token, "guest");
        persistOfflineGuestUuidFromSessionToken(res.token);
      } catch {
        /* e.g. production build without dev login — leave unsigned-in */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, token, setAuth]);

  useEffect(() => {
    if (!hydrated || !token) return;
    persistOfflineGuestUuidFromSessionToken(token);
  }, [hydrated, token]);

  return null;
}

function AmbientBgmBridge() {
  useAmbientBgm();
  return null;
}

/**
 * Auto-mute audio while the tab is hidden/inactive and restore on return.
 * Uses the non-persistent platform suspend gate so user mute/volume prefs are
 * preserved exactly when the tab becomes visible again.
 */
function TabVisibilityAudioBridge() {
  useEffect(() => {
    if (typeof document === "undefined") return;

    let suspended = false;

    const apply = () => {
      const hidden = document.hidden;
      if (hidden && !suspended) {
        suspended = true;
        beginPlatformAudioSuspend();
        syncBgmPlaybackVolume();
      } else if (!hidden && suspended) {
        suspended = false;
        endPlatformAudioSuspend();
        syncBgmPlaybackVolume();
      }
    };

    apply();
    document.addEventListener("visibilitychange", apply);
    return () => {
      document.removeEventListener("visibilitychange", apply);
      if (suspended) {
        suspended = false;
        endPlatformAudioSuspend();
        syncBgmPlaybackVolume();
      }
    };
  }, []);
  return null;
}

export function Providers({ children }: { children?: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5_000,
            refetchOnWindowFocus: true,
          },
        },
      }),
  );
  return (
    <QueryClientProvider client={client}>
      <FrvrRoot />
      <SessionHydration />
      <EnsureGuestSession />
      <ProfileSetupGate />
      <AmbientBgmBridge />
      <TabVisibilityAudioBridge />
      <UiTapSoundBridge />
      {children ?? <Outlet />}
    </QueryClientProvider>
  );
}
